/**
 * PostHog Analytics
 *
 * Centralized initialization and event tracking for conversion & retention funnels.
 * See docs/patterns/posthog-events.md for the full event taxonomy.
 *
 * Dual-fire: every PostHog call is also dispatched (best-effort) to the
 * Coffee Visitor-ID product-usage pixel (`window.coffee.*`) when
 * `VITE_COFFEE_USAGE_ENABLED === 'true'`. PostHog calls always run
 * first and unconditionally; the coffee.* call is wrapped in its own
 * try/catch so a throw inside the visitor-id pixel can never break
 * Auth0 login / existing tracking. See plan
 * docs/archive/implementation-plans/visitor-id-product-usage.md §5.
 */

import posthog from 'posthog-js';

const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';
const ENVIRONMENT = import.meta.env.MODE;

const COFFEE_USAGE_ENABLED = import.meta.env.VITE_COFFEE_USAGE_ENABLED === 'true';

let initialized = false;

declare global {
  interface Window {
    coffee?: {
      init?: (opts?: { autocapture?: boolean }) => void;
      track?: (name: string, props?: Record<string, unknown>) => void;
      identify?: (distinctId: string, traits?: Record<string, unknown>) => void;
      page?: (props?: Record<string, unknown>) => void;
      reset?: () => void;
      optOut?: () => void;
    };
  }
}

// Each dual-fire is wrapped in its own try/catch — a throw in the
// visitor-id pixel must never propagate back into PostHog flow or
// Auth0 callbacks. Returns void; failures are silent by design.
function dualFireTrack(eventName: string, properties?: Record<string, unknown>) {
  if (!COFFEE_USAGE_ENABLED) return;
  try {
    window.coffee?.track?.(eventName, properties);
  } catch {
    // swallow — dual-fire is best-effort
  }
}

function dualFireIdentify(distinctId: string, traits?: Record<string, unknown>) {
  if (!COFFEE_USAGE_ENABLED) return;
  try {
    window.coffee?.identify?.(distinctId, traits);
  } catch {
    // swallow
  }
}

function dualFireReset() {
  if (!COFFEE_USAGE_ENABLED) return;
  try {
    window.coffee?.reset?.();
  } catch {
    // swallow
  }
}

export function initPostHog() {
  if (!POSTHOG_KEY) {
    console.log('PostHog key not configured, skipping initialization');
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    ui_host: 'https://us.posthog.com',
    capture_pageview: false, // SPA — we fire $pageview on route changes in the provider
    capture_pageleave: true,
    autocapture: true,
    capture_exceptions: true,
    capture_performance: {
      web_vitals: true, // emits $web_vitals events (LCP, FCP, CLS, INP) for page-load monitoring
    },
    persistence: 'localStorage+cookie',
    debug: ENVIRONMENT === 'development',
  });

  initialized = true;

  // Initialize the Coffee VID pixel SDK explicitly. Autocapture is ON
  // for the dogfood so the VID pixel captures the same $pageview
  // stream Posthog SaaS already captures on coffeecrm route changes —
  // gives us apples-to-apples pageview data in product_usage_events to
  // validate the pixel's data fidelity against Posthog. Safe to patch
  // history.pushState here because posthog-js runs with
  // capture_pageview: false (above), so it doesn't patch pushState
  // itself — VID is the only patcher in our app.
  if (COFFEE_USAGE_ENABLED) {
    try {
      window.coffee?.init?.({ autocapture: true });
    } catch {
      // swallow
    }
  }
}

export function trackEvent(eventName: string, properties?: Record<string, unknown>) {
  if (!initialized) return;
  posthog.capture(eventName, properties);
  dualFireTrack(eventName, properties);
}

export function identifyUser(user: {
  id: string;
  email?: string | null;
  name?: string | null;
  role?: string | null;
  workspaceId?: string | null;
}) {
  if (!initialized) return;

  posthog.identify(user.id, {
    email: user.email ?? undefined,
    name: user.name ?? undefined,
    role: user.role ?? undefined,
  });

  if (user.workspaceId) {
    posthog.group('workspace', user.workspaceId);
  }

  dualFireIdentify(user.id, {
    email: user.email ?? undefined,
    name: user.name ?? undefined,
    role: user.role ?? undefined,
    workspaceId: user.workspaceId ?? undefined,
  });
}

export function resetPostHog() {
  if (!initialized) return;
  posthog.reset();
  dualFireReset();
}

export { posthog };
